import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import PageContainer from '../components/common/PageContainer';
import PageHeader from '../components/common/PageHeader';
import PageSection from '../components/common/PageSection';

const PatientDashboard: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    return (
        <PageContainer>
            <PageHeader 
                title="My Dashboard" 
                emoji="🏠"
                description={user?.displayName ? `Welcome back, ${user.displayName}` : "Your personal wellness hub"}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Quick Actions */}
                <PageSection title="Quick Actions" emoji="⚡">
                    <div className="space-y-3">
                        <button 
                            onClick={() => navigate('/chat')}
                            className="w-full p-3 bg-purple-100 hover:bg-purple-200 rounded-lg text-left transition-colors"
                        >
                            <div className="flex items-center space-x-3">
                                <span className="text-2xl">💬</span>
                                <div>
                                    <div className="font-medium text-gray-900">Start Chat</div>
                                    <div className="text-sm text-gray-600">Connect with your therapist</div>
                                </div>
                            </div>
                        </button>
                        <button 
                            onClick={() => navigate('/mood-tracker')}
                            className="w-full p-3 bg-green-100 hover:bg-green-200 rounded-lg text-left transition-colors"
                        >
                            <div className="flex items-center space-x-3">
                                <span className="text-2xl">😊</span>
                                <div>
                                    <div className="font-medium text-gray-900">Log Mood</div>
                                    <div className="text-sm text-gray-600">Track how you're feeling</div>
                                </div>
                            </div>
                        </button>
                        <button 
                            onClick={() => navigate('/wellness')}
                            className="w-full p-3 bg-blue-100 hover:bg-blue-200 rounded-lg text-left transition-colors"
                        >
                            <div className="flex items-center space-x-3">
                                <span className="text-2xl">🌱</span>
                                <div>
                                    <div className="font-medium text-gray-900">Wellness Tools</div>
                                    <div className="text-sm text-gray-600">Access self-care resources</div>
                                </div>
                            </div>
                        </button>
                    </div>
                </PageSection>

                {/* Recent Activity */}
                <PageSection title="Recent Activity" emoji="📈">
                    <div className="space-y-3">
                        <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <span className="text-lg">💬</span>
                                    <span className="text-sm font-medium">Chat Session</span>
                                </div>
                                <span className="text-xs text-gray-500">2 hours ago</span>
                            </div>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <span className="text-lg">😊</span>
                                    <span className="text-sm font-medium">Mood Logged</span>
                                </div>
                                <span className="text-xs text-gray-500">Yesterday</span>
                            </div>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <span className="text-lg">🌱</span>
                                    <span className="text-sm font-medium">Wellness Exercise</span>
                                </div>
                                <span className="text-xs text-gray-500">3 days ago</span>
                            </div>
                        </div>
                    </div>
                </PageSection>

                {/* Wellness Overview */}
                <PageSection title="Wellness Overview" emoji="🌱">
                    <div className="space-y-4">
                        <div className="text-center">
                            <div className="text-3xl font-bold text-green-600">7</div>
                            <div className="text-sm text-gray-600">Day Streak</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-center">
                            <div className="p-3 bg-green-50 rounded-lg">
                                <div className="text-lg font-semibold text-green-700">85%</div>
                                <div className="text-xs text-green-600">Mood Score</div>
                            </div>
                            <div className="p-3 bg-blue-50 rounded-lg">
                                <div className="text-lg font-semibold text-blue-700">12</div>
                                <div className="text-xs text-blue-600">Sessions</div>
                            </div>
                        </div>
                    </div>
                </PageSection>
            </div>

            {/* Upcoming Appointments */}
            <PageSection title="Upcoming Appointments" emoji="📅">
                <div className="space-y-3">
                    <div className="p-4 bg-blue-50 border-l-4 border-blue-400 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-medium text-gray-900">Therapy Session</div>
                                <div className="text-sm text-gray-600">with Dr. Smith</div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm font-medium text-blue-700">Tomorrow</div>
                                <div className="text-xs text-gray-500">2:00 PM</div>
                            </div>
                        </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-medium text-gray-900">Check-in</div>
                                <div className="text-sm text-gray-600">Weekly progress review</div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm font-medium text-gray-700">Next Week</div>
                                <div className="text-xs text-gray-500">Friday 10:00 AM</div>
                            </div>
                        </div>
                    </div>
                </div>
            </PageSection>
        </PageContainer>
    );
};

export default PatientDashboard;
